/**
 * MCPServerListUserLevel - Component for displaying and managing user-level MCP server configurations
 *
 * User-level servers are available across ALL of the user's projects (no projectId required).
 *
 * Features:
 * - Display list of user-level MCP servers (project_id=null)
 * - Health status indicators (connected/disconnected/error/unknown)
 * - Add, Edit, Delete server actions
 * - Refresh tools functionality
 * - Empty state when no servers configured
 * - Scope indicator badge showing "Available in all projects"
 * - Loading and error states
 *
 * Technical Design: /workspace/docs/technical/TDD-AC-784-MCP-Scoping.md Section 3.5.3
 * Ticket: AC-790
 * Epic: AC-785
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  RefreshCw,
  Globe,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle,
  Loader,
} from 'lucide-react';
import apiService from '../../services/api';
import { MCPServer, MCPHealthStatus, MCPServerTemplate } from '../../types';
import MCPServerConfig from './MCPServerConfig';
import MCPServerTemplates from './MCPServerTemplates';
import Tooltip from '../common/Tooltip';

/**
 * Health status badge component
 */
const HealthStatusBadge: React.FC<{ status: MCPHealthStatus; error?: string }> = ({
  status,
  error,
}) => {
  const getStatusConfig = (status: MCPHealthStatus) => {
    switch (status) {
      case 'connected':
        return {
          icon: <CheckCircle className="w-4 h-4" data-testid="check-circle-icon" />,
          label: 'Connected',
          className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        };
      case 'disconnected':
        return {
          icon: <XCircle className="w-4 h-4" data-testid="x-circle-icon" />,
          label: 'Disconnected',
          className: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
        };
      case 'error':
        return {
          icon: <AlertCircle className="w-4 h-4" data-testid="alert-circle-icon" />,
          label: 'Error',
          className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        };
      case 'unknown':
      default:
        return {
          icon: <HelpCircle className="w-4 h-4" data-testid="help-circle-icon" />,
          label: 'Unknown',
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className="flex flex-col">
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.className}`}
      >
        {config.icon}
        {config.label}
      </span>
      {error && status === 'error' && (
        <span className="text-xs text-red-600 mt-1">{error}</span>
      )}
    </div>
  );
};

/**
 * Scope badge component indicating server availability
 */
const ScopeBadge: React.FC = () => {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
      <Globe className="w-3 h-3" data-testid="globe-icon" />
      All Operations
    </span>
  );
};

const MCPServerListUserLevel: React.FC = () => {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingServer, setEditingServer] = useState<MCPServer | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<MCPServerTemplate | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [deleteConfirmServer, setDeleteConfirmServer] = useState<MCPServer | null>(null);
  const [refreshingServerId, setRefreshingServerId] = useState<number | null>(null);

  /**
   * Load user-level MCP servers from API
   */
  const loadServers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.listUserLevelMCPServers();
      setServers(data);
    } catch (err: any) {
      setError(err.message || 'Error loading MCP servers');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServers();
  }, [loadServers]);

  /**
   * Handle opening templates panel or config modal for new server
   */
  const handleAddServer = () => {
    setEditingServer(null);
    setSelectedTemplate(null);
    setShowTemplates(true);
  };

  /**
   * Handle selecting a template
   */
  const handleSelectTemplate = (template: MCPServerTemplate) => {
    setSelectedTemplate(template);
    setShowTemplates(false);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle creating from scratch (no template)
   */
  const handleCreateFromScratch = () => {
    setSelectedTemplate(null);
    setShowTemplates(false);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle opening config modal for editing existing server
   */
  const handleEditServer = (server: MCPServer) => {
    setEditingServer(server);
    setIsConfigModalOpen(true);
  };

  /**
   * Handle closing config modal
   */
  const handleCloseModal = () => {
    setIsConfigModalOpen(false);
    setEditingServer(null);
    setSelectedTemplate(null);
  };

  /**
   * Handle closing templates panel
   */
  const handleCloseTemplates = () => {
    setShowTemplates(false);
  };

  /**
   * Handle successful save from config modal
   */
  const handleSaveServer = () => {
    loadServers();
  };

  /**
   * Handle delete server - first show confirmation
   */
  const handleDeleteClick = (server: MCPServer) => {
    setDeleteConfirmServer(server);
  };

  /**
   * Confirm delete and execute
   */
  const handleConfirmDelete = async () => {
    if (!deleteConfirmServer) return;

    try {
      await apiService.deleteUserLevelMCPServer(deleteConfirmServer.mcp_server_id);
      setDeleteConfirmServer(null);
      loadServers();
    } catch (err: any) {
      setError(err.message || 'Failed to delete server');
      setDeleteConfirmServer(null);
    }
  };

  /**
   * Cancel delete confirmation
   */
  const handleCancelDelete = () => {
    setDeleteConfirmServer(null);
  };

  /**
   * Handle refresh tools for a server
   */
  const handleRefreshTools = async (server: MCPServer) => {
    try {
      setRefreshingServerId(server.mcp_server_id);
      await apiService.discoverUserLevelMCPTools(server.mcp_server_id);
      loadServers();
    } catch (err: any) {
      setError(err.message || 'Failed to refresh tools');
    } finally {
      setRefreshingServerId(null);
    }
  };

  // Loading state - show spinner when loading initially
  if (isLoading && servers.length === 0) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader data-testid="loader-icon" className="w-6 h-6 animate-spin text-primary-600" />
      </div>
    );
  }

  // Error state
  if (error && servers.length === 0) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <div className="flex items-center gap-2 text-red-800">
          <AlertCircle className="w-5 h-5" />
          <span>Error loading MCP servers: {error}</span>
        </div>
      </div>
    );
  }

  // Empty state
  if (!isLoading && servers.length === 0) {
    return (
      <div className="space-y-6">
        <div className="text-center py-4">
          <Globe className="w-12 h-12 mx-auto text-gray-400 mb-4" data-testid="globe-icon" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No user-level MCP servers configured</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            These servers will be available in all your projects.
          </p>
        </div>

        {/* Add Server button in empty state */}
        <div className="text-center">
          <button
            onClick={handleAddServer}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <Plus className="w-4 h-4" data-testid="plus-icon" />
            Add Server
          </button>
        </div>

        {/* Templates Modal for empty state */}
        {showTemplates && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Add MCP Server</h2>
                <button
                  onClick={handleCloseTemplates}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded"
                  aria-label="Close"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <p className="text-sm text-primary-600 bg-primary-50 p-3 rounded-lg mb-4">
                  <Globe className="w-4 h-4 inline mr-2" />
                  This server will be available in all your projects.
                </p>
                <MCPServerTemplates onSelectTemplate={handleSelectTemplate} />
                <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Or configure a custom MCP server manually:
                  </p>
                  <button
                    onClick={handleCreateFromScratch}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    <Globe className="w-4 h-4" />
                    Configure Manually
                  </button>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                <button
                  onClick={handleCloseTemplates}
                  className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Config Modal for empty state */}
        {isConfigModalOpen && (
          <MCPServerConfig
            scope="user"
            server={editingServer || undefined}
            template={selectedTemplate || undefined}
            onClose={handleCloseModal}
            onSave={handleSaveServer}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Add Server button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white">User-Level MCP Servers</h3>
        <button
          onClick={handleAddServer}
          className="inline-flex items-center gap-2 px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <Plus data-testid="plus-icon" className="w-4 h-4" />
          Add Server
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2 text-red-800 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Server list */}
      <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" role="table">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Server
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Transport
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Scope
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-cap-dark divide-y divide-gray-200 dark:divide-gray-700">
            {servers.map((server) => (
              <tr key={server.mcp_server_id} className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900">
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary-500" data-testid="globe-icon" />
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-warm-white">{server.name}</div>
                      {server.description && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">{server.description}</div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                    {server.transport_type}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <ScopeBadge />
                </td>
                <td className="px-4 py-4">
                  <HealthStatusBadge
                    status={server.health_status}
                    error={server.last_error || undefined}
                  />
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content="Refresh Tools">
                      <button
                        onClick={() => handleRefreshTools(server)}
                        disabled={refreshingServerId === server.mcp_server_id}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                        aria-label="Refresh Tools"
                      >
                        <RefreshCw
                          data-testid="refresh-icon"
                          className={`w-4 h-4 ${
                            refreshingServerId === server.mcp_server_id ? 'animate-spin' : ''
                          }`}
                        />
                      </button>
                    </Tooltip>
                    <Tooltip content="Edit">
                      <button
                        onClick={() => handleEditServer(server)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded"
                        aria-label="Edit"
                      >
                        <Edit2 data-testid="edit-icon" className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete">
                      <button
                        onClick={() => handleDeleteClick(server)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                        aria-label="Delete"
                      >
                        <Trash2 data-testid="trash-icon" className="w-4 h-4" />
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteConfirmServer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-4">Delete MCP Server</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete &quot;{deleteConfirmServer.name}&quot;? This action
              cannot be undone. This server will be removed from all projects.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
                aria-label="Confirm Delete"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplates && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="templates-modal-title"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 id="templates-modal-title" className="text-lg font-semibold text-gray-900 dark:text-warm-white">
                Add MCP Server
              </h2>
              <button
                onClick={handleCloseTemplates}
                className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded"
                aria-label="Close"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <p className="text-sm text-primary-600 bg-primary-50 p-3 rounded-lg mb-4">
                <Globe className="w-4 h-4 inline mr-2" />
                This server will be available in all your projects.
              </p>

              <MCPServerTemplates onSelectTemplate={handleSelectTemplate} />

              {/* Manual Configuration Option */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Or configure a custom MCP server manually:
                </p>
                <button
                  onClick={handleCreateFromScratch}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  <Globe className="w-4 h-4" />
                  Configure Manually
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <button
                onClick={handleCloseTemplates}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Modal */}
      {isConfigModalOpen && (
        <MCPServerConfig
          scope="user"
          server={editingServer || undefined}
          template={selectedTemplate || undefined}
          onClose={handleCloseModal}
          onSave={handleSaveServer}
        />
      )}
    </div>
  );
};

export default MCPServerListUserLevel;
